import { useRef, useState, useEffect } from 'react';
import * as THREE from 'three';
import { ANIMATION_SPEED } from '../../constants/config';
import type { ResponsiveValues } from '../../types';
import { useAppStore } from '../../store/useAppStore';

interface SpiralAnimationReturn {
  scrollRef: React.RefObject<number>;
  initAnimation: number;
  setInitAnimation: React.Dispatch<React.SetStateAction<number>>;
  isInitialAnimationComplete: React.MutableRefObject<boolean>;
  hasScrolled: boolean;
  velocity: React.RefObject<number>;
}

export const useSpiralAnimation = (
  _groupRef: React.RefObject<THREE.Group>,
  _values: ResponsiveValues
): SpiralAnimationReturn => {
  const scrollRef = useRef<number>(0);
  const [initAnimation, setInitAnimation] = useState<number>(0);
  const isInitialAnimationComplete = useRef<boolean>(false);
  const velocity = useRef<number>(0);
  const isScrolling = useRef<boolean>(false);
  const selectedProject = useAppStore(state => state.selectedProject);
  const scrollOffset = useAppStore(state => state.scrollOffset);
  const [hasScrolled] = useState(false);

  const MAX_VELOCITY = 0.01;

  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      if (selectedProject || !isInitialAnimationComplete.current) {
        return;
      }

      event.preventDefault();
      useAppStore.getState().setHasScrolled(true);

      // Calculer la nouvelle vélocité avec une transition plus douce
      const delta = event.deltaY * ANIMATION_SPEED.SCROLL;
      const currentVelocity = velocity.current;

      // Si on approche de la limite, réduire progressivement l'impact du delta
      const distanceToLimit = Math.abs(currentVelocity) / MAX_VELOCITY;
      const dampingFactor = Math.max(0, 1 - distanceToLimit);

      const newVelocity = currentVelocity + delta * dampingFactor;
      velocity.current = Math.min(
        Math.max(newVelocity, -MAX_VELOCITY),
        MAX_VELOCITY
      );
      isScrolling.current = true;
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => window.removeEventListener('wheel', handleWheel);
  }, [selectedProject]);

  useEffect(() => {
    if (scrollOffset !== undefined) {
      const MIN_SCROLL_VALUE = 0.03;

      const animate = () => {
        const diff = scrollOffset - (scrollRef.current || 0);

        if (Math.abs(diff) > MIN_SCROLL_VALUE) {
          const step = diff * 0.1;
          const limitedStep = Math.min(
            Math.max(step, -MAX_VELOCITY),
            MAX_VELOCITY
          );
          scrollRef.current = scrollRef.current + limitedStep;
          requestAnimationFrame(animate);
        } else {
          scrollRef.current = scrollOffset;
        }
      };
      animate();
    }
  }, [scrollOffset]);

  useEffect(() => {
    const animate = () => {
      if (isScrolling.current) {
        const nextScroll = scrollRef.current + velocity.current;
        scrollRef.current = (nextScroll + Math.PI * 2) % (Math.PI * 2);

        velocity.current *= 0.975;

        if (Math.abs(velocity.current) < 0.00001) {
          isScrolling.current = false;
          velocity.current = 0;
        }
      }
      requestAnimationFrame(animate);
    };

    animate();
    return () => {
      isScrolling.current = false;
    };
  }, []);

  return {
    scrollRef,
    initAnimation,
    setInitAnimation,
    isInitialAnimationComplete,
    hasScrolled,
    velocity,
  };
};
